// apps/api/src/qxcircle/qxcircle-comment-write.ts
// Description: QXCircle comment create helper — keeps service under line cap.
// Owner: Masa Bayu
// Date: 2026-08-24

import { BadRequestException, NotFoundException } from "./_nestjs-common";
import type {
  CreateQxCircleCommentInput,
  QxCircleComment,
  QxCircleFeedPost,
} from "./qxcircle.types";
import { QXCIRCLE_COMMENT_BODY_MAX } from "./qxcircle.types";

export function createQxCircleCommentRecord(
  postId: string,
  input: CreateQxCircleCommentInput,
  comments: QxCircleComment[],
  commentSeq: number,
): { comment: QxCircleComment; nextSeq: number } {
  const body = input.body?.trim() ?? "";
  if (!body) {
    throw new BadRequestException("Comment body is required");
  }
  if (body.length > QXCIRCLE_COMMENT_BODY_MAX) {
    throw new BadRequestException(
      `Comment body max ${QXCIRCLE_COMMENT_BODY_MAX} characters`,
    );
  }
  const parentId = resolveQxCircleParentCommentId(postId, input.parentId, comments);
  const nextSeq = commentSeq + 1;
  const authorName = input.authorName?.trim() || 'Member';
  const handleRaw = input.authorHandle?.trim();
  if (!handleRaw) {
    throw new BadRequestException('Comment author handle is required');
  }
  const authorHandle = handleRaw.startsWith('@') ? handleRaw : `@${handleRaw}`;
  if (!input.ownerAccountId) {
    throw new BadRequestException('Comment owner account is required');
  }
  const comment: QxCircleComment = {
    id: `qxcircle_cmt_local_${nextSeq}`,
    postId,
    parentId,
    authorName,
    authorHandle,
    ownerAccountId: input.ownerAccountId,
    body,
    canManage: true,
    createdAt: new Date().toISOString(),
    updatedAt: null,
  };
  return { comment, nextSeq };
}

export function syncQxCirclePostCommentCount(
  post: QxCircleFeedPost,
  comments: QxCircleComment[],
): void {
  post.commentCount = comments.filter((c) => c.postId === post.id).length;
}

function resolveQxCircleParentCommentId(
  postId: string,
  parentId: string | null | undefined,
  comments: QxCircleComment[],
): string | null {
  if (!parentId) return null;
  const parent = comments.find((c) => c.id === parentId);
  if (!parent || parent.postId !== postId) {
    throw new NotFoundException(`Parent comment not found: ${parentId}`);
  }
  return parent.parentId ?? parent.id;
}
